import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Clock, AlertTriangle, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface SlaRow {
  ticket_id: string;
  sla_tier: string;
  response_due_at: string;
  resolution_due_at: string;
  first_response_at: string | null;
  response_breached: boolean;
  resolution_breached: boolean;
}

interface SlaBadgeProps {
  ticketId: string;
  ticketStatus?: string;
  size?: "sm" | "md";
  showLabel?: boolean;
  className?: string;
}

const formatRemaining = (ms: number): string => {
  const abs = Math.abs(ms);
  const mins = Math.floor(abs / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hours < 24) return remMins > 0 ? `${hours}h ${remMins}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`;
};

const tierLabel = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

export const SlaBadge = ({ ticketId, ticketStatus, size = "sm", showLabel = false, className }: SlaBadgeProps) => {
  const [sla, setSla] = useState<SlaRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data } = await (supabase as any)
        .from("support_ticket_sla")
        .select("*")
        .eq("ticket_id", ticketId)
        .maybeSingle();
      if (mounted) {
        setSla(data as SlaRow | null);
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [ticketId]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  if (loading || !sla) return null;

  const isResolved = ticketStatus === "resolved";
  // Pick which deadline is active
  const responded = !!sla.first_response_at;
  const targetIso = isResolved
    ? sla.resolution_due_at
    : (responded ? sla.resolution_due_at : sla.response_due_at);
  const targetMs = new Date(targetIso).getTime();
  const diff = targetMs - now;
  const breached = diff < 0;
  const stage = isResolved ? "Resolved" : (responded ? "Resolution" : "First response");

  let toneClass = "bg-success/15 text-success border-success/30";
  let Icon = CheckCircle2;

  if (isResolved) {
    toneClass = "bg-muted text-muted-foreground border-border";
    Icon = CheckCircle2;
  } else if (breached) {
    toneClass = "bg-destructive/15 text-destructive border-destructive/40 animate-pulse";
    Icon = AlertTriangle;
  } else if (diff < 60 * 60 * 1000) {
    // <1h remaining → warning
    toneClass = "bg-warning/15 text-warning border-warning/40";
    Icon = Clock;
  } else {
    toneClass = "bg-info/15 text-info border-info/30";
    Icon = Clock;
  }

  const text = isResolved
    ? `SLA met`
    : breached
      ? `Breached · ${formatRemaining(diff)} over`
      : `${formatRemaining(diff)} left`;

  const sizing = size === "md" ? "text-xs px-2.5 py-1" : "text-[10px] px-2 py-0.5";
  const iconSize = size === "md" ? "h-3.5 w-3.5" : "h-3 w-3";

  return (
    <Badge
      variant="outline"
      className={cn("gap-1 font-medium inline-flex items-center", toneClass, sizing, className)}
      title={`${tierLabel(sla.sla_tier)} SLA · ${stage} due ${new Date(targetIso).toLocaleString()}`}
    >
      <Icon className={iconSize} />
      {showLabel && <span className="opacity-80">{tierLabel(sla.sla_tier)}:</span>}
      <span>{text}</span>
    </Badge>
  );
};

export default SlaBadge;
